import { useMemo } from 'react';
import { getTreeMapDisplayText } from '../../util/formatterFunctions';

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [70, 131, 230];
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ];
}

const calculateColorShade = (
  value: number,
  maxValue: number,
  primaryColor: string
): string => {
  const normalizedValue = value / maxValue;
  const [pr, pg, pb] = hexToRgb(primaryColor);

  const lightR = Math.round(pr + (255 - pr) * 0.55);
  const lightG = Math.round(pg + (255 - pg) * 0.55);
  const lightB = Math.round(pb + (255 - pb) * 0.55);

  const darkR = Math.round(pr * 0.45);
  const darkG = Math.round(pg * 0.45);
  const darkB = Math.round(pb * 0.45);

  const r = Math.round(lightR - normalizedValue * (lightR - darkR));
  const g = Math.round(lightG - normalizedValue * (lightG - darkG));
  const b = Math.round(lightB - normalizedValue * (lightB - darkB));

  return `rgb(${r},${g},${b})`;
};

function convertAmount(amount: any) {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}m`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(2)}k`;
  } else {
    return amount.toString();
  }
}

const canFitText = (text: string, width: number, fontSize: number) => {
  const approxCharWidth = fontSize * 0.6;
  return text.length * approxCharWidth <= width - 12;
};

export const CustomizedContent = (props: {
  root: any;
  depth: number;
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
  payload: any;
  colors: string[];
  rank: number;
  name: string;
  onClick: any;
  selectedCell: string[];
  value: any;
  enableDarkTheme?: boolean;
  totalCost: number;
  costPerTon: number;
  costPerEngineHr: number;
  costPerMile: number;
  primaryColor?: string;
}) => {
  const {
    root,
    depth,
    x,
    y,
    width,
    height,
    name,
    onClick,
    selectedCell,
    value,
    enableDarkTheme = false,
    totalCost,
    costPerTon,
    costPerEngineHr,
    costPerMile,
    primaryColor = '#4683E6',
  } = props;
  const padding = 4;
  const handleClick = () => {
    onClick(name);
  };
  const isSelected = selectedCell.includes(name);

  const displayText = useMemo(() => {
    const temp = getTreeMapDisplayText(name, width, height);
    return temp;
  }, [width, height, name]);

  const costRows: string[] = [];
  if (totalCost) costRows.push(`${convertAmount(totalCost)}`);
  if (costPerTon) costRows.push(`Per ton - ${costPerTon}`);
  if (costPerEngineHr) costRows.push(`Per eng hr - ${costPerEngineHr}`);
  if (costPerMile) costRows.push(`Per mile - ${costPerMile}`);

  const visibleCostRows: string[] = [];
  const lineHeight = 16;
  const maxLines = Math.floor(height / lineHeight) - 1;
  for (const row of costRows) {
    if (visibleCostRows.length >= maxLines) break;
    if (canFitText(row, width, 14)) {
      visibleCostRows.push(row);
    }
  }

  return (
    <g onClick={handleClick}>
      <rect
        x={x + padding}
        y={y + padding}
        width={width - 2 * padding}
        height={height - 2 * padding}
        style={{
          fill: calculateColorShade(
            value,
            root?.children
              ? Math.max(...root.children.map((child: any) => child.value))
              : value,
            primaryColor
          ),
          strokeDasharray: isSelected ? '4 2' : 'none',
          stroke: isSelected
            ? '#FFA726'
            : enableDarkTheme
            ? '#0A192D'
            : '#ffffff',
          strokeWidth: isSelected ? 2 : 0,
          strokeOpacity: isSelected ? 1 : 0,
        }}
        rx={4}
        ry={4}
      />
      {depth === 1 ? (
        <text
          x={x + width / 2}
          y={y + height / 2 + 7}
          textAnchor="middle"
          fill="#fff"
          fontSize={14}
          style={{
            stroke: '#ffffff',
          }}
        >
          {displayText}
        </text>
      ) : null}

      {depth === 1 &&
        visibleCostRows.map((txt, i) => (
          <text
            key={i}
            x={x + 6}
            y={y + 20 + i * lineHeight}
            fill="#fff"
            fontSize={12 * 0.9}
            fillOpacity={0.9}
            style={{ stroke: '#ffffff' }}
          >
            {txt}
          </text>
        ))}
    </g>
  );
};
